// Country to Cell ID Mapping
// Maps world countries to war effort cell IDs
// This allows each country to be clickable and associated with a cell

export const countryToCellMap = {
  // North America
  'United States': 212,
  'Canada': 213,
  'Mexico': 214,
  'Guatemala': 215,
  'Belize': 216,
  'El Salvador': 217,
  'Honduras': 218,
  'Nicaragua': 220,
  'Costa Rica': 222,
  'Panama': 223,
  'Cuba': 224,
  'Jamaica': 225,
  'Haiti': 227,
  'Dominican Republic': 250,
  
  // South America
  'Brazil': 251,
  'Argentina': 252,
  'Chile': 253,
  'Peru': 254,
  'Colombia': 255,
  'Venezuela': 256,
  'Ecuador': 257,
  'Bolivia': 258,
  'Paraguay': 259,
  'Uruguay': 260,
  'Guyana': 261,
  'Suriname': 262,
  'French Guiana': 263,
  
  // Europe
  'United Kingdom': 264,
  'France': 265,
  'Germany': 266,
  'Italy': 267,
  'Spain': 268,
  'Poland': 269,
  'Ukraine': 270,
  'Russia': 289,
  'Russian Federation': 289, // Alternative name for Russia in SVG
  'Sweden': 290,
  'Norway': 291,
  'Finland': 292,
  'Denmark': 293,
  'Netherlands': 294,
  'Belgium': 295,
  'Switzerland': 296,
  'Austria': 298,
  'Czech Republic': 299,
  'Portugal': 300,
  'Greece': 301,
  'Romania': 302,
  'Hungary': 303,
  'Bulgaria': 304,
  'Croatia': 305,
  'Serbia': 306,
  'Ireland': 307,
  'Iceland': 308,
  'Turkey': 309,
  // Additional European countries
  'Albania': 271,
  'Armenia': 272,
  'Azerbaijan': 273,
  'Belarus': 274,
  'Bosnia and Herzegovina': 275,
  'Estonia': 276,
  'Georgia': 277,
  'Latvia': 278,
  'Lithuania': 279,
  'Luxembourg': 280,
  'Moldova': 281,
  'Montenegro': 282,
  'North Macedonia': 283,
  'Slovakia': 284,
  'Slovenia': 285,
  'Cyprus': 286,
  'Malta': 287,
  'Kosovo': 288,
  
  // Asia
  'China': 332,
  'India': 333,
  'Japan': 334,
  'South Korea': 335,
  'Indonesia': 336,
  'Thailand': 339,
  'Vietnam': 340,
  'Philippines': 341,
  'Malaysia': 342,
  'Singapore': 343,
  'Myanmar': 344,
  'Cambodia': 345,
  'Laos': 346,
  'Bangladesh': 347,
  'Pakistan': 348,
  'Afghanistan': 372,
  'Iran': 373,
  'Iraq': 374,
  'Saudi Arabia': 375,
  'United Arab Emirates': 378,
  'Israel': 379,
  'Jordan': 380,
  'Lebanon': 381,
  'Syria': 382,
  'Kazakhstan': 383,
  'Uzbekistan': 384,
  'Mongolia': 385,
  'Nepal': 386,
  'Sri Lanka': 387,
  // Additional Asian countries
  'Brunei': 349,
  'Bhutan': 350,
  'Kyrgyzstan': 351,
  'Tajikistan': 352,
  'Turkmenistan': 353,
  'Taiwan': 354,
  'Palestine': 355,
  'North Korea': 356,
  
  // Africa
  'Egypt': 413,
  'Libya': 414,
  'Algeria': 415,
  'Morocco': 416,
  'Tunisia': 418,
  'Sudan': 419,
  'Ethiopia': 420,
  'Kenya': 421,
  'Tanzania': 422,
  'South Africa': 423,
  'Nigeria': 424,
  'Ghana': 425,
  'Congo': 426,
  'Angola': 454,
  'Zambia': 455,
  'Zimbabwe': 456,
  'Madagascar': 457,
  'Mozambique': 459,
  'Cameroon': 460,
  'Ivory Coast': 461,
  'Senegal': 462,
  'Mali': 463,
  'Niger': 464,
  'Chad': 465,
  'Somalia': 466,
  'Uganda': 467,
  'Rwanda': 468,
  // Additional African countries
  'Burundi': 417,
  'Botswana': 427,
  'Central African Republic': 428,
  'Democratic Republic of the Congo': 429,
  'Lesotho': 430,
  'Malawi': 431,
  'Namibia': 432,
  'South Sudan': 433,
  'Western Sahara': 434,
  'Comoros': 435,
  'Mauritius': 436,
  'Seychelles': 437,
  'Guinea-Bissau': 438,
  
  // Oceania
  'Australia': 495,
  'New Zealand': 496,
  'Papua New Guinea': 497,
  'Fiji': 500,
  'New Caledonia': 501,
  'Solomon Islands': 502,
  'Vanuatu': 505,
  'Samoa': 506,
  'Tonga': 507,
  'Micronesia': 508,
  'Palau': 509,
  
  // Additional countries
  'Greenland': 535,
  'Mauritania': 536,
  'Burkina Faso': 540,
  'Guinea': 541,
  'Sierra Leone': 542,
  'Liberia': 545,
  'Togo': 546,
  'Benin': 547,
  'Gabon': 548,
  'Equatorial Guinea': 549,
  'Eritrea': 575,
  'Djibouti': 576,
  'Yemen': 587,
  'Oman': 588,
  'Qatar': 589,
  'Kuwait': 615,
  'Bahrain': 616
};

// Reverse mapping: cell ID to country name
export const cellToCountryMap = Object.fromEntries(
  Object.entries(countryToCellMap).map(([country, cellId]) => [cellId, country])
);

// Get country name from cell ID
export const getCountryFromCellId = (cellId, t = null) => {
  const countryName = cellToCountryMap[cellId];
  if (!countryName) {
    return `Cell ${cellId}`;
  }
  // If translation function is provided, use it
  if (t) {
    const translationKey = `countries.${countryName.replace(/\s+/g, '')}`;
    const translated = t(translationKey);
    // If translation exists (not the key itself), return it
    if (translated && translated !== translationKey) {
      return translated;
    }
  }
  return countryName;
};

// Get cell ID from country name
export const getCellIdFromCountry = (countryName) => {
  return countryToCellMap[countryName] || null;
};

// Get all countries
export const getAllCountries = () => {
  return Object.keys(countryToCellMap);
};

// Get all cell IDs that are mapped to countries
export const getAllMappedCellIds = () => {
  return Object.values(countryToCellMap);
};

